import React, { useState, useEffect } from "react";
import { getAnalysis } from "../../services/analysisService.js";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logoutSuccess } from "../../authentication/AuthActions.js";
import VerticalNavigation from "../../components/verticalNavigation/verticalNavigation.tsx";
import DocumentUpload from "../../components/documentUpload/documentUpload.tsx";
import UserManagement from "../../components/userManagement/userManagement.tsx";
import DownloadedReports from "../../components/downloadedReports/downloadedReports.tsx";
import AddUser from "../../components/addUser/addUser.tsx";
import DeleteUser from "../../components/deleteUser/deleteUser.tsx";
import { getAllUsers } from "../services/userManagementService";
import { useResetAll } from "../../data/useResetAll.ts";
import "./welcome.css";

interface WelcomeProps {
  isAdmin?: boolean;
}

const Welcome: React.FC<WelcomeProps> = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state: any) => state.auth.user)?.user;
  const resetAll = useResetAll();
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    (user && user.role == "Admin") ? setIsAdmin(true) : setIsAdmin(false);
  }, [user]);
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [isDocumentUploadOpen, setIsDocumentUploadOpen] =
    useState<boolean>(false);
  const [isUserManagementOpen, setIsUserManagementOpen] =
    useState<boolean>(false);
  const [isDownloadedReportsOpen, setIsDownloadedReportsOpen] =
    useState<boolean>(false);
  const [isAddUserOpen, setIsAddUserOpen] = useState<boolean>(false);
  const [isDeleteUserOpen, setIsDeleteUserOpen] = useState<boolean>(false);
  const [editUserData, setEditUserData] = useState<any>(null);
  const [deleteUserData, setDeleteUserData] = useState<any>(null);

  const handleLaunchAnalysis = () => {
    resetAll();
    navigate("/goal-setting");
  };

  const handleLogout = () => {
    dispatch(logoutSuccess());
    navigate("/start");
  };

  const handleDocumentUpload = () => {
    setIsDocumentUploadOpen(true);
  };

  const handleUserManagement = () => {
    setIsUserManagementOpen(true);
  };

  const handleDownloadedReports = () => {
    setIsDownloadedReportsOpen(true);
  };

  const handleAddNewUser = () => {
    setIsUserManagementOpen(false);
    setEditUserData(null);
    setIsAddUserOpen(true);
  };

  const handleEditUser = (userData: any) => {
    setIsUserManagementOpen(false);
    setEditUserData(userData);
    setIsAddUserOpen(true);
  };

  const handleDeleteUser = (userData: any) => {
    setIsUserManagementOpen(false);
    setDeleteUserData(userData);
    setIsDeleteUserOpen(true);
  };

  const handleConfirmDelete = (userId: number) => {
    console.log("Delete user with ID:", userId);
  };

  const adminButtons = isAdmin
    ? [
      { label: "Document Upload →", onClick: handleDocumentUpload },
      { label: "User Management →", onClick: handleUserManagement },
      { label: "Downloaded Reports →", onClick: handleDownloadedReports },
    ]
    : undefined;

  const [analysisData, setAnalysisData] = useState<any[]>([]);

  useEffect(() => {
    const fetchAnalyses = async () => {
      if (user) {
        try {
          const response = await getAnalysis({});
          console.log(response.data);
          if (response.data) {
            setAnalysisData(response.data);
          }
        } catch (error) {
          console.error("Failed to fetch analyses", error);
        }
      } else {
        setAnalysisData([]);
      }
    };
    fetchAnalyses();
  }, [user]);

  return (
    <div className="welcome-page">
      <VerticalNavigation
        title={isAdmin ? "Admin Dashboard" : "Welcome!"}
        description={
          isAdmin
            ? "Manage system settings, users, and documents. Monitor all analyses and system performance."
            : "Pick up where you left off or explore new waste-to-value ideas.\n\nYou can edit your previous analyses by clicking on the pins on the map, or you can start fresh by pressing the 'Launch a New Analysis' button."
        }
        buttons={adminButtons}
        showProgressBar={false}
        showBack={false}
        onMenuToggle={setIsMenuOpen}
      />

      {!isMenuOpen && (
        <main className="welcome-main">
          <div className="welcome-container">
            <div className="welcome-analyses-grid">
              {analysisData.map((item, index) => {
                const params = item.parameters || {};
                const results = item.results || [];

                // Sort results by feasibilityIndex descending to show best options
                const sortedResults = [...results].sort((a: any, b: any) => (b.feasibilityIndex || 0) - (a.feasibilityIndex || 0));

                const topResult = sortedResults[0];
                const secondResult = sortedResults[1];
                const thirdResult = sortedResults[2];
                const plantName = params.wtfName || "Unknown Plant";
                const analysisName = item.name || "Unnamed Analysis";
                const country = params.countryName || "Unknown Country";
                const region = params.region || "Unknown Region";
                const district = params.district || "Unknown District";
                const wasteType = params.wasteType || "Unknown Waste";
                const analysisType = params.valueAddedProduct || "Analysis";

                const category1 = topResult ? topResult.businessModel : "-";
                const value1 = topResult ? `${topResult.feasibilityIndex}` : "-";
                const category2 = secondResult ? secondResult.businessModel : "-";
                const value2 = secondResult ? `${secondResult.feasibilityIndex}` : "-";
                const category3 = thirdResult ? thirdResult.businessModel : "-";
                const value3 = thirdResult ? `${thirdResult.feasibilityIndex}` : "-";

                return (
                  <div
                    key={index}
                    className="welcome-analysis-card"
                    onClick={() => navigate("/mcda-results", { state: { payload: item.parameters } })}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="welcome-analysis-left">
                      <div className="welcome-analysis-location">
                        {country} {" > "} {region} {" > "} {district}
                      </div>
                      <div className="welcome-analysis-name">
                        {plantName}
                      </div>
                      <div className="welcome-analysis-type">
                        {"Analysis Name: "}{analysisName}
                      </div>
                      <div className="welcome-analysis-type">
                        {analysisType}
                      </div>
                      <div className="welcome-analysis-waste">
                        {wasteType}
                      </div>
                    </div>
                    <div className="welcome-analysis-right">
                      <div className="welcome-feasibility-header">
                        Feasibility Index
                      </div>
                      <div className="welcome-analysis-row">
                        <div className="welcome-category-name">
                          {category1}
                        </div>
                        <div className="welcome-value welcome-value-rank-1">
                          {value1}
                        </div>
                      </div>
                      <div className="welcome-analysis-row">
                        <div className="welcome-category-name">
                          {category2}
                        </div>
                        <div className="welcome-value welcome-value-rank-2">
                          {value2}
                        </div>
                      </div>
                      <div className="welcome-analysis-row">
                        <div className="welcome-category-name">
                          {category3}
                        </div>
                        <div className="welcome-value welcome-value-rank-3">
                          {value3}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            {!isAdmin && (
              <button
                className="welcome-launch-button"
                onClick={handleLaunchAnalysis}
              >
                Launch a New Analysis →
              </button>
            )}
          </div>
        </main>
      )}

      <DocumentUpload
        isOpen={isDocumentUploadOpen}
        onClose={() => setIsDocumentUploadOpen(false)}
      />

      <UserManagement
        isOpen={isUserManagementOpen}
        onClose={() => setIsUserManagementOpen(false)}
        onAddNewUser={handleAddNewUser}
        onEditUser={handleEditUser}
        onDeleteUser={handleDeleteUser}
      />

      <DownloadedReports
        isOpen={isDownloadedReportsOpen}
        onClose={() => setIsDownloadedReportsOpen(false)}
      />

      <AddUser
        isOpen={isAddUserOpen}
        onClose={() => setIsAddUserOpen(false)}
        editMode={editUserData !== null}
        userData={editUserData}
      />

      <DeleteUser
        isOpen={isDeleteUserOpen}
        onClose={() => setIsDeleteUserOpen(false)}
        userData={deleteUserData}
        onConfirmDelete={handleConfirmDelete}
      />
    </div>
  );
};

export default Welcome;
